import DraggableStepCard from 'pages/AutomationEngine/Engine/Steps/DraggableStepCard';
import type {
  GenericOption,
  SmartConnectorFlowLoad,
  SmartConnectorStatus,
} from 'pages/SmartConnectors/types';
import { type FC, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FlowBuilderContext } from '../../context';
import { SMART_CONNECTOR_STATUSES } from 'pages/SmartConnectors/constants';
import SmartConnectorService from 'services/SmartConnectorService';
import useModal from '__components/Modals/useModal';
import ConfirmationModal from '__components/Modals/ConfirmationModal';
import { useToast, toastVariant } from '__components/ToastProvider';
import type { FlowStep } from '../../types';
import { getIsSmartConnectorActive } from 'pages/SmartConnectors/helpers';
import { isCSVLoadStep } from 'pages/SmartConnectors/SmartConnector/helpers';

export const RunSmartConnectorCard: FC<{
  step: FlowStep;
  color: string;
}> = ({ step, color, ...others }) => {
  const { t } = useTranslation();
  const [showToast] = useToast();
  const {
    smartConnector,
    refetchSmartConnector,
    flowErrors,
    setFlowErrors,
    showDeactivationModalWithResult,
    smartConnectors,
    dropStep,
  } = useContext(FlowBuilderContext);

  const [smartConnectorToRun, menuOptions] = useMemo(() => {
    const smartConnectorToRun = smartConnectors.find(
      (sc) => sc.id === step.details.smart_connector_to_run
    );

    const menuOptions = [
      {
        label: t('Edit'),
        value: 'edit',
      },
      {
        label: t('Delete'),
        value: 'delete',
      },
    ];
    return [smartConnectorToRun, menuOptions];
  }, [step.details.smart_connector_to_run, smartConnectors, t]);

  const stepErrors = flowErrors.flow.loads?.[step.order] || {};
  const hasErrors = Object.values(stepErrors).length;

  const onDelete = async (status: SmartConnectorStatus) => {
    try {
      await SmartConnectorService.updateSmartConnector(smartConnector.id, {
        flow: {
          loads: smartConnector.flow.loads.reduce<{
            loads: Partial<SmartConnectorFlowLoad>[];
            count: number;
          }>(
            (acc, load) => {
              if (load.id !== step.details.id) {
                // filter out load to be deleted. We don't want to touch other loads, but we need to pass them to update order,
                // so we pass only id, custom_object and order for rest values to not to be touched and validated on BE
                acc.loads.push({
                  id: load.id,
                  order: acc.count++,
                  ...(isCSVLoadStep(load)
                    ? { custom_object: load.custom_object }
                    : {}),
                });
              }
              return acc;
            },
            { loads: [], count: 0 }
          ).loads,
        },
        status,
      });
      setFlowErrors((prev) => ({
        ...prev,
        flow: {
          ...prev.flow,
          loads: (prev.flow.loads || []).filter((_, i) => i !== step.order),
        },
      }));
      await refetchSmartConnector();
      return true;
    } catch (error) {
      showToast({
        message: t('Failed to delete upload step'),
        variant: toastVariant.FAILURE,
      });
      return false;
    }
  };

  const onConfirmDelete = () => {
    if (
      getIsSmartConnectorActive({
        status: smartConnector.status,
      })
    ) {
      return showDeactivationModalWithResult({
        handleSave: () => onDelete(SMART_CONNECTOR_STATUSES.inactive),
      });
    }
    return onDelete(smartConnector.status);
  };

  const [modalProps, , { show: showModal }] = useModal({
    handleSubmit: onConfirmDelete,
  });

  const onSelectMenu = ({ value }: GenericOption) => {
    if (value === 'edit') {
      dropStep({
        step,
        branch: false,
        leaf: false,
        label: '',
        id: '',
        parentKey: null,
        index: step.order,
      });
    }
    if (value === 'delete') {
      showModal();
    }
  };

  return (
    <>
      <DraggableStepCard
        {...others}
        id={step.id}
        color={hasErrors ? 'var(--color-button-warning-hover)' : color}
        borderColor={
          hasErrors ? 'var(--color-button-warning-hover)' : undefined
        }
        showMovingPlaceholder={false}
        title={t('Run SmartConnector')}
        menuOptions={menuOptions}
        onSelectMenu={onSelectMenu}
        isSmartConnectorFlow
      >
        {smartConnectorToRun?.name || t('[Deleted]')}
      </DraggableStepCard>
      {modalProps.show ? (
        <ConfirmationModal
          heading={t('Please Confirm Deletion')}
          {...modalProps}
        >
          {t(
            'This will delete the Run SmartConnector step, its configuration, and the associated return scopes.'
          )}
        </ConfirmationModal>
      ) : null}
    </>
  );
};
